import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { useAuth } from "../lib/auth.jsx";
import { getSupabase } from "../lib/supabase";
import { enableWebPush } from "../lib/push";
import { setLocale } from "../lib/i18n";
import { useTranslation } from "react-i18next";
import { CURRENCY_SYMBOLS } from "./AdminDashboard";

export function ProfileScreen({ theme, onToggleTheme }) {
  const { profile, session, signOut, authedFetch } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(profile?.full_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [allergy, setAllergy] = useState(profile?.allergy_notes || "");
  const [skinTone, setSkinTone] = useState(profile?.skin_tone || "");
  const [country, setCountry] = useState(profile?.country || "");
  const [stateRegion, setStateRegion] = useState(profile?.state_region || "");
  const [timezone, setTimezone] = useState(profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const [magicEmail, setMagicEmail] = useState(session?.user?.email || "");
  const [magicSent, setMagicSent] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { t, i18n } = useTranslation();
  const [pushOk, setPushOk] = useState("");
  const [membership, setMembership] = useState(null);
  const [twofa, setTwofa] = useState({ enabled: false });
  const [twofaSetup, setTwofaSetup] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [paymentGateway, setPaymentGateway] = useState("stripe");
  const [plans, setPlans] = useState(null);       // live prices from DB
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [vendorPlans, setVendorPlans] = useState({});
  const [vendorStudio, setVendorStudio] = useState(null);
  const [vendorStudioName, setVendorStudioName] = useState("");
  const [vendorKey, setVendorKey] = useState("");
  const [vendorBusy, setVendorBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const m = await authedFetch("/api/subscriptions/me"); setMembership(m);
        const tf = await authedFetch("/api/2fa/status"); setTwofa(tf);
        const curr = await authedFetch("/api/settings/currency");
        setPaymentGateway(profile?.preferred_payment_gateway === "opay" ? "opay" : "stripe");
        if (curr?.currency) setCurrencyCode(curr.currency.toUpperCase());
        const p = await authedFetch("/api/subscriptions/plans");
        if (p?.plans) setPlans(p.plans);
        await loadVendorAccess();
      } catch {}
    })();
  }, [profile?.preferred_payment_gateway]);

  async function loadVendorAccess() {
    const out = await authedFetch("/api/vendor/me").catch(() => null);
    if (!out) return;
    setVendorPlans(out.plans || {});
    setVendorStudio(out.vendorStudio || null);
    setVendorStudioName(out.vendorStudio?.display_name || "");
  }

  async function startMembership(tier) {
    try {
      const endpoint = paymentGateway === "opay"
        ? "/api/payments/opay/subscription"
        : "/api/subscriptions/checkout";
      const out = await authedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({
          tier,
          currency: paymentGateway === "opay" ? undefined : currencyCode,
        }),
      });
      window.location.href = out.url;
    } catch (e) { setSavedMsg(e.message); }
  }
  async function cancelMembership() {
    if (!confirm("Cancel your membership at the end of the period?")) return;
    try { await authedFetch("/api/subscriptions/cancel", { method: "POST" });
      const m = await authedFetch("/api/subscriptions/me"); setMembership(m);
    } catch (e) { setSavedMsg(e.message); }
  }
  async function redeemVendorKey() {
    setVendorBusy("redeem"); setSavedMsg("");
    try {
      const out = await authedFetch("/api/vendor/license/redeem", {
        method: "POST",
        body: JSON.stringify({ key: vendorKey, studioName: vendorStudioName || name }),
      });
      setVendorStudio(out.vendorStudio || null);
      setVendorStudioName(out.vendorStudio?.display_name || vendorStudioName);
      setVendorKey("");
      setSavedMsg("Vendor access activated.");
    } catch (e) {
      setSavedMsg(e.message);
    } finally {
      setVendorBusy("");
    }
  }
  async function saveVendorStudioName() {
    if (!vendorStudioName.trim()) {
      setSavedMsg("Studio name is required.");
      return;
    }
    setVendorBusy("save"); setSavedMsg("");
    try {
      const out = await authedFetch("/api/vendor/studio", {
        method: "PATCH",
        body: JSON.stringify({ displayName: vendorStudioName }),
      });
      setVendorStudio(out.vendorStudio || null);
      setSavedMsg("Studio name updated.");
    } catch (e) {
      setSavedMsg(e.message);
    } finally {
      setVendorBusy("");
    }
  }
  async function startVendorCheckout(interval) {
    setVendorBusy(interval); setSavedMsg("");
    try {
      const out = await authedFetch("/api/vendor/checkout", {
        method: "POST",
        body: JSON.stringify({ interval, studioName: vendorStudioName || name }),
      });
      window.location.href = out.url;
    } catch (e) {
      setSavedMsg(e.message);
      setVendorBusy("");
    }
  }
  async function enablePush() {
    setPushOk("");
    try { await enableWebPush(); setPushOk("Push enabled — we'll ping you about your bookings."); }
    catch (e) { setPushOk(e.message); }
  }
  async function startTwofa() {
    try { const out = await authedFetch("/api/2fa/setup", { method: "POST" }); setTwofaSetup(out); }
    catch (e) { setSavedMsg(e.message); }
  }
  async function verifyTwofa() {
    try { await authedFetch("/api/2fa/verify", { method: "POST", body: JSON.stringify({ code: twofaCode }) });
      setTwofaSetup(null); setTwofaCode(""); setTwofa({ enabled: true });
    } catch (e) { setSavedMsg(e.message); }
  }
  async function disableTwofa() {
    if (!confirm("Disable two-factor authentication?")) return;
    try { await authedFetch("/api/2fa/disable", { method: "POST" }); setTwofa({ enabled: false }); }
    catch (e) { setSavedMsg(e.message); }
  }

  useEffect(() => {
    setName(profile?.full_name || "");
    setPhone(profile?.phone || "");
    setAllergy(profile?.allergy_notes || "");
    setSkinTone(profile?.skin_tone || "");
    setCountry(profile?.country || "");
    setStateRegion(profile?.state_region || "");
    setTimezone(profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  }, [profile]);

  useEffect(() => {
    (async () => {
      try { const out = await authedFetch("/api/notifications/prefs"); setPrefs(out.prefs); }
      catch {}
    })();
  }, []);

  async function saveProfile() {
    setSavingProfile(true); setSavedMsg("");
    try {
      await authedFetch("/api/account/profile", {
        method: "PATCH",
        body: JSON.stringify({
          full_name: name, phone,
          allergy_notes: allergy, skin_tone: skinTone,
          country, state_region: stateRegion, timezone,
          preferred_payment_gateway: paymentGateway,
        }),
      });
      setSavedMsg("Saved.");
    } catch (e) { setSavedMsg(e.message); }
    setSavingProfile(false);
  }

  async function savePrefs(patch) {
    setSavingPrefs(true);
    try {
      const next = { ...prefs, ...patch };
      setPrefs(next);
      await authedFetch("/api/notifications/prefs", { method: "PATCH", body: JSON.stringify(patch) });
    } catch (e) { console.warn(e); }
    setSavingPrefs(false);
  }

  async function sendMagic() {
    try {
      await authedFetch("/api/auth/magic-link", { method: "POST", body: JSON.stringify({ email: magicEmail }) });
      setMagicSent(true);
    } catch {}
  }

  async function exportData() {
    const text = await authedFetch("/api/account/export");
    const blob = new Blob([typeof text === "string" ? text : JSON.stringify(text, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `oge-data-${session.user.id.slice(0,8)}.json`;
    a.click(); URL.revokeObjectURL(url);
  }

  async function deleteAccount() {
    setDeleting(true);
    try {
      await authedFetch("/api/account", { method: "DELETE" });
      await signOut();
      nav("/");
    } catch (e) { alert(e.message); setDeleting(false); }
  }

  async function loginWith(provider) {
    const sb = await getSupabase();
    await sb.auth.signInWithOAuth({ provider, options: { redirectTo: window.location.origin } });
  }

  const planName = (membership?.tier || "free").replace(/_/g, " ");
  const profileInitial = (name || profile?.email || "O").trim()[0]?.toUpperCase() || "O";
  const panelStyle = {
    maxWidth: "none",
    margin: 0,
    borderRadius: 20,
    padding: 22,
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1060, margin: "0 auto", padding: "30px 16px" }}>
        <section style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
          gap: 18,
          alignItems: "center",
          marginBottom: 20,
          padding: 22,
          borderRadius: 26,
          background: "linear-gradient(135deg, rgba(196,135,154,0.16), rgba(201,168,76,0.10))",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-sm)",
        }}>
          <div style={{
            width: 74,
            height: 74,
            borderRadius: 24,
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
            color: "#fff",
            fontSize: 30,
            fontWeight: 900,
            boxShadow: "0 18px 46px rgba(196,135,154,0.28)",
          }}>
            {profileInitial}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.4, color: "var(--accent)", textTransform: "uppercase" }}>
              Account control center
            </div>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(32px, 6vw, 52px)", lineHeight: 1, margin: "6px 0 0", color: "var(--text)" }}>
              Your profile
            </h1>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              <ProfilePill label={`${planName} plan`} />
              <ProfilePill label={twofa.enabled ? "2FA enabled" : "2FA off"} />
              <ProfilePill label={currencyCode} />
            </div>
          </div>
        </section>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 16, alignItems: "start" }}>
        <Card style={{ ...panelStyle, marginBottom: 18 }}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Personal info</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <Input label="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Input label="Allergies / sensitivities" hint="e.g. acrylic-free, fragrance-free, latex allergy"
              value={allergy} onChange={(e) => setAllergy(e.target.value)} />
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>
                Race / Ethnicity
              </span>
              <select
                value={skinTone}
                onChange={(e) => setSkinTone(e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid var(--border-strong)",
                  borderRadius: 14,
                  padding: "13px 14px",
                  background: "var(--bg-input)",
                  color: "var(--text)",
                  fontSize: 15,
                  outline: "none",
                }}
              >
                <option value="">Not specified</option>
                <option value="white">White / Caucasian</option>
                <option value="black">Black / African</option>
                <option value="asian">East Asian</option>
                <option value="south_asian">South Asian</option>
                <option value="hispanic">Hispanic / Latina</option>
                <option value="middle_eastern">Middle Eastern</option>
                <option value="mixed">Mixed / Multiracial</option>
                <option value="other">Other / Prefer not to say</option>
              </select>
              <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Optional — personalises AI hand renders.</span>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              <Input label="Country" value={country} onChange={(e) => setCountry(e.target.value)} />
              <Input label="State / Province" value={stateRegion} onChange={(e) => setStateRegion(e.target.value)} />
              <Input label="Time zone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center" }}>
            {savedMsg && <span style={{ color: "var(--text-confirm)", fontSize: 12 }}>{savedMsg}</span>}
            <Button label="Save" variant="primary" onClick={saveProfile} loading={savingProfile} />
          </div>
        </Card>

        <div style={{ display: "grid", gap: 16 }}>
        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Notifications</h3>
          {!prefs ? <div>Loading…</div> : (
            <div style={{ display: "grid", gap: 8 }}>
              {[
                ["email_enabled", "Email notifications"],
                ["push_enabled",  "Push notifications"],
                ["sms_enabled",   "SMS reminders (Twilio)"],
                ["reminder_24h",  "24-hour appointment reminder"],
                ["reminder_2h",   "2-hour appointment reminder"],
                ["marketing_enabled", "Marketing & special offers"],
              ].map(([k, l]) => (
                <label key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 14, color: "var(--text)" }}>{l}</span>
                  <input type="checkbox" checked={!!prefs[k]} onChange={(e) => savePrefs({ [k]: e.target.checked })} disabled={savingPrefs} />
                </label>
              ))}
            </div>
          )}
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Sign-in methods</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
            Add another way to sign in next time.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button label="Send magic link to my email" icon="✉️" variant="outline" onClick={sendMagic} />
            <Button label="Continue with Apple" variant="dark" onClick={() => loginWith("apple")} />
            <Button label="Continue with Google" variant="ghost" onClick={() => loginWith("google")} />
          </div>
          {magicSent && <div style={{ marginTop: 10, color: "var(--text-confirm)", fontSize: 13 }}>Magic link sent. Check your email.</div>}
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>
            Apple and Google sign-in can be connected for faster access on trusted devices.
          </p>
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Data & privacy</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button label="Download my data" icon="⬇️" variant="outline" onClick={exportData} />
            <Button label="Delete my account" variant="danger" onClick={() => setDeleteOpen(true)} />
          </div>
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>
            Your bookings, designs, payments, and notifications will be permanently deleted.
          </p>
        </Card>
        </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 16, marginTop: 16 }}>
        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Membership</h3>
          {membership && (
            <div>
              <div style={{ marginBottom: 12, fontSize: 13, color: "var(--text-muted)" }}>
                Current tier: <strong style={{ color: "var(--text)", textTransform: "capitalize" }}>{membership.tier}</strong>
                {membership.renewsAt && <> · renews {new Date(membership.renewsAt).toLocaleDateString()}</>}
              </div>
              {country === "NG" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                  {[
                    ["stripe", "Stripe"],
                    ["opay", "OPay"],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      label={label}
                      variant={paymentGateway === value ? "gold" : "outline"}
                      size="sm"
                      onClick={() => setPaymentGateway(value)}
                    />
                  ))}
                </div>
              )}
              {membership.tier === "free" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 10 }}>
                  <PlanCard
                    tier="silver" name="Silver"
                    price={formatPlanPrice(plans?.silver, currencyCode)}
                    perks={["10% off bookings", "Priority slots"]}
                    onPick={() => startMembership("silver")}
                  />
                  <PlanCard
                    tier="gold" name="Gold"
                    price={formatPlanPrice(plans?.gold, currencyCode)}
                    perks={["20% off bookings", "Member-only designs", "Free travel up to 5 mi"]}
                    onPick={() => startMembership("gold")}
                    highlight
                  />
                </div>
              ) : (
                <Button label="Cancel membership" variant="danger" onClick={cancelMembership} />
              )}
            </div>
          )}
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Vendor studio access</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
            Run this same booking system under your own studio name with a subscription or owner-issued access key.
          </p>
          {vendorStudio ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Status: <strong style={{ color: "var(--text)", textTransform: "capitalize" }}>{vendorStudio.subscription_status || "active"}</strong>
                {vendorStudio.current_period_end && <> · renews {new Date(vendorStudio.current_period_end).toLocaleDateString()}</>}
              </div>
              <Input
                label="Studio display name"
                value={vendorStudioName}
                onChange={(e) => setVendorStudioName(e.target.value)}
              />
              <Button label="Save studio name" variant="outline" onClick={saveVendorStudioName} loading={vendorBusy === "save"} />
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <Input
                label="Your studio name"
                value={vendorStudioName}
                onChange={(e) => setVendorStudioName(e.target.value)}
                placeholder="My Nail Studio"
              />
              <Input
                label="Access key"
                value={vendorKey}
                onChange={(e) => setVendorKey(e.target.value.toUpperCase())}
                placeholder="OGE-XXXXX-XXXXX-XXXXX-XXXXX"
              />
              <Button
                label="Redeem key"
                variant="gold"
                onClick={redeemVendorKey}
                disabled={!vendorKey.trim()}
                loading={vendorBusy === "redeem"}
              />
            </div>
          )}
          <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
            {Object.entries(vendorPlans).map(([interval, plan]) => (
              <div key={interval} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                borderTop: "1px solid var(--border)",
                paddingTop: 10,
                flexWrap: "wrap",
              }}>
                <div>
                  <div style={{ fontWeight: 800, color: "var(--text)" }}>{formatVendorInterval(interval)}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatVendorPrice(plan)}</div>
                </div>
                <Button
                  label="Subscribe"
                  size="sm"
                  variant="outline"
                  onClick={() => startVendorCheckout(interval)}
                  loading={vendorBusy === interval}
                />
              </div>
            ))}
          </div>
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Browser push</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
            Get reminders even when this tab isn't open.
          </p>
          <Button label="Enable browser notifications" icon="🔔" variant="outline" onClick={enablePush} />
          {pushOk && <div style={{ fontSize: 13, color: "var(--text-confirm)", marginTop: 10 }}>{pushOk}</div>}
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Two-factor authentication</h3>
          {twofa.enabled ? (
            <Button label="Disable 2FA" variant="danger" onClick={disableTwofa} />
          ) : twofaSetup ? (
            <>
              <p style={{ fontSize: 13, color: "var(--text-soft)", marginBottom: 10 }}>
                Scan this QR with Google Authenticator, 1Password, or any TOTP app, then enter the 6-digit code.
              </p>
              <img src={twofaSetup.qrDataUrl} alt="2FA QR" style={{ width: 200, height: 200, background: "#fff", borderRadius: 12, padding: 8, display: "block", marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <Input value={twofaCode} onChange={(e) => setTwofaCode(e.target.value)} placeholder="123 456" style={{ width: 140, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                <Button label="Verify" variant="primary" onClick={verifyTwofa} disabled={twofaCode.length < 6} />
              </div>
            </>
          ) : (
            <Button label="Enable 2FA" icon="🔐" variant="primary" onClick={startTwofa} />
          )}
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>{t("language")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["en", "English"], ["es", "Español"], ["fr", "Français"]].map(([k, l]) => (
              <Button key={k} label={l}
                variant={i18n.language === k ? "primary" : "outline"}
                onClick={() => setLocale(k)} />
            ))}
          </div>
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Theme</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
            Choose your preferred color scheme
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              label="Light"
              icon="☀️"
              variant={theme === "light" ? "primary" : "outline"}
              onClick={() => theme !== "light" && onToggleTheme()}
            />
            <Button
              label="Dark"
              icon="🌙"
              variant={theme === "dark" ? "primary" : "outline"}
              onClick={() => theme !== "dark" && onToggleTheme()}
            />
          </div>
        </Card>

        <Card style={panelStyle}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Session</h3>
          <Button label="Sign out" variant="outline" onClick={async () => { await signOut(); nav("/"); }} />
        </Card>
        </div>

        {deleteOpen && (
          <div role="dialog" aria-modal="true" onClick={() => !deleting && setDeleteOpen(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: "var(--bg-elevated)", color: "var(--text)",
              borderRadius: 16, padding: 24, width: "100%", maxWidth: 460,
              border: "1px solid var(--border)",
            }}>
              <h3 style={{ fontFamily: "var(--font-display)", marginBottom: 8 }}>Delete account?</h3>
              <p style={{ fontSize: 14, color: "var(--text-soft)", marginBottom: 16 }}>
                This is permanent. Type <b>DELETE</b> below to confirm.
              </p>
              <ConfirmDelete onConfirm={deleteAccount} onCancel={() => setDeleteOpen(false)} loading={deleting} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Format a plan's price using the studio's currency — e.g. "₦19.00/mo" */
function formatPlanPrice(plan, currencyCode = "USD") {
  if (!plan) {
    // Fallback while plans are loading
    const sym = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    const defaults = { silver: 1900, gold: 4900 };
    return sym; // will be blank during load, filled once plans arrive
  }
  const code = (plan.currency || currencyCode).toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] || code;
  const amount = (plan.priceCents / 100).toFixed(2);
  return `${sym}${amount}/quarter`;
}

function formatVendorInterval(interval = "") {
  return ({
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    semiannual: "Every 6 months",
    annual: "Annual",
  })[interval] || String(interval || "Plan").replace(/_/g, " ");
}

function formatVendorPrice(plan) {
  if (!plan) return "";
  const sym = CURRENCY_SYMBOLS[String(plan.currency || "USD").toUpperCase()] || String(plan.currency || "USD").toUpperCase();
  const suffix = plan.intervalCount && plan.intervalCount > 1
    ? `/${plan.intervalCount} ${plan.interval}s`
    : `/${plan.interval || "period"}`;
  return `${sym}${(Number(plan.priceCents || 0) / 100).toFixed(2)}${suffix}`;
}

function PlanCard({ name, price, perks, onPick, highlight }) {
  return (
    <div style={{
      borderRadius: 14, padding: 14,
      background: highlight ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
      color: highlight ? "#fff" : "var(--text)", border: "1px solid var(--border)",
    }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700 }}>{name}</div>
      <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{price}</div>
      <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 16, opacity: 0.85, lineHeight: 1.7 }}>
        {perks.map((p, i) => <li key={i}>{p}</li>)}
      </ul>
      <button onClick={onPick} style={{
        marginTop: 10, padding: "8px 14px", borderRadius: 999, fontWeight: 600, cursor: "pointer",
        background: highlight ? "#fff" : "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
        color: highlight ? "var(--mauve-deep)" : "#fff", border: "none", fontSize: 12,
      }}>Choose {name}</button>
    </div>
  );
}

function ProfilePill({ label }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      minHeight: 30,
      borderRadius: 999,
      padding: "6px 11px",
      background: "var(--bg-elevated)",
      border: "1px solid var(--border)",
      color: "var(--text)",
      fontSize: 12,
      fontWeight: 800,
      textTransform: "capitalize",
    }}>
      {label}
    </span>
  );
}

function ConfirmDelete({ onConfirm, onCancel, loading }) {
  const [v, setV] = useState("");
  return (
    <>
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="DELETE" />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button label="Cancel" variant="outline" onClick={onCancel} />
        <Button label="Delete forever" variant="danger" onClick={onConfirm} disabled={v !== "DELETE" || loading} loading={loading} />
      </div>
    </>
  );
}
